import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Search as SearchIcon, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import { InspectionPanel } from "@/features/quality/InspectionPanel";
import {
  decisionTone,
  inspectionDetailQuery,
  inspectionFilters,
  inspectionListQuery,
  isInspectionFilter,
  listInspections,
  statusTone,
  type InspectionFilter,
  type InspectionRow,
} from "@/features/quality/inspections";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";
import { inspectionListColumns, inspectionStatuses, inspectionOrderTriggerValues as inspectionTriggers } from "@patronix/domain/list";
import { listView, noView, viewQuery, viewSearch, type ViewSearch } from "@/lib/listView";

// Quality → Inspection orders of the active company: created by receipts and
// production reports with an inspection plan, or on demand. Keyset (created
// desc) through inspection_order_page; detail = one document RPC, prefetched
// on hover. Computed documents: export only, no import (ADR-034).

interface Search extends ViewSearch {
  status: InspectionFilter;
  q: string;
  id?: string | undefined;
}

export const Route = createFileRoute("/_app/quality/inspection-orders")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      status: isInspectionFilter(raw.status) ? raw.status : "open",
      q: typeof raw.q === "string" ? raw.q.slice(0, 100) : "",
    };
    if (typeof raw.id === "string" && raw.id) s.id = raw.id;
    return { ...s, ...viewSearch(inspectionListColumns, raw) };
  },
  loaderDeps: ({ search }) => ({ status: search.status, q: search.q, f: search.f, s: search.s }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(inspectionListQuery({ companyId, status: deps.status, q: deps.q, ...viewQuery(inspectionListColumns, deps) }));
  },
  component: InspectionOrdersPage,
});

function InspectionOrdersPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canDecide = useCan("quality.inspection.decide");
  const { company } = useActiveCompany();
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const view = listView(inspectionListColumns, search, setSearch);
  const params = { companyId: company?.id ?? "", status: search.status, q: search.q, ...view.query };
  const query = useInfiniteQuery(inspectionListQuery(params));
  const [text, setText] = useState(search.q);
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text, id: undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const columns: Column<InspectionRow>[] = [
    {
      key: "number",
      header: t("quality.ins.colNumber"),
      width: "minmax(120px,0.8fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.number}</span>,
    },
    {
      key: "item",
      header: t("quality.plan.item"),
      width: "minmax(180px,1.5fr)",
      cell: (r) => (
        <span className="truncate">
          <span className="font-mono text-xs">{r.item_code}</span> <span className="text-ink-soft">{r.item_description}</span>
        </span>
      ),
    },
    { key: "lot", header: t("quality.ins.lot"), width: "minmax(90px,0.7fr)", cell: (r) => <span className="font-mono text-xs">{r.lot_code ?? "—"}</span> },
    {
      key: "qty",
      header: t("quality.ins.qty"),
      width: "minmax(90px,0.6fr)",
      align: "end",
      cell: (r) => (
        <span className="tabular">
          {fmt.qty(String(r.qty))} {r.base_uom_code}
        </span>
      ),
    },
    {
      key: "source",
      header: t("quality.ins.source"),
      width: "minmax(120px,0.9fr)",
      className: "text-ink-soft",
      cell: (r) => (r.source_number ? <span className="font-mono text-xs">{r.source_number}</span> : t(`quality.ins.sources.${r.source_type}`)),
      options: inspectionTriggers.map((v) => ({ value: v, label: t(`quality.triggers.${v}`) })),
    },
    {
      key: "progress",
      header: t("quality.ins.results"),
      width: "minmax(90px,0.6fr)",
      align: "end",
      cell: (r) => (
        <span className={cn("tabular", r.nok_count > 0 && "text-(--status-danger-fg)")}>
          {r.entered_count}/{r.result_count}
        </span>
      ),
    },
    {
      key: "status",
      header: t("po.colStatus"),
      width: "minmax(110px,0.8fr)",
      cell: (r) =>
        r.decision ? (
          <Badge tone={decisionTone[r.decision]}>{t(`quality.ins.decision.${r.decision}`)}</Badge>
        ) : (
          <Badge tone={statusTone[r.status]}>{t(`quality.ins.status.${r.status}`)}</Badge>
        ),
      options: inspectionStatuses.map((v) => ({ value: v, label: t(`quality.ins.status.${v}`) })),
    },
  ];
  const exportColumns: ExportColumn<InspectionRow>[] = [
    { header: t("quality.ins.colNumber"), kind: "code", value: (r) => r.number },
    { header: "item_code", kind: "code", value: (r) => r.item_code },
    { header: t("stock.colDescription"), kind: "text", value: (r) => r.item_description },
    { header: "lot", kind: "code", value: (r) => r.lot_code },
    { header: "qty", kind: "number", value: (r) => Number(r.qty), unit: (r) => r.base_uom_code },
    { header: "uom", kind: "code", value: (r) => r.base_uom_code },
    { header: "warehouse", kind: "code", value: (r) => r.warehouse_code },
    { header: "source", kind: "code", value: (r) => r.source_number ?? r.source_type },
    { header: "status", kind: "code", value: (r) => r.status },
    { header: "decision", kind: "code", value: (r) => r.decision },
    { header: "created_at", kind: "datetime", value: (r) => r.created_at },
  ];
  const label = (f: InspectionFilter) => (f === "all" ? t("po.filterAll") : t(`quality.ins.filter.${f}`));

  const listTools: ListTools<InspectionRow> = {
    id: "inspection-orders",
    title: t("quality.ins.title"),
    onNew: canDecide ? () => setSearch({ id: "new" }) : undefined,
    export: { fileBase: "inspection-orders", sheet: t("quality.ins.title"), columns: exportColumns, fetchPage: (cursor, signal) => listInspections(params, cursor, signal, EXPORT_PAGE) },
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.quality.title")} <span aria-hidden="true">/</span> {t("nav.quality.inspectionOrders")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("quality.ins.title")}</h1>
        </div>
        {canDecide && (
          <button
            type="button"
            onClick={() => setSearch({ id: "new" })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("quality.ins.new")}
          </button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("po.colStatus")} className="flex flex-wrap gap-2">
          {inspectionFilters.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={search.status === f}
              onClick={() => setSearch({ status: f, id: undefined })}
              className={cn(
                "h-8 rounded-full border px-3 text-[13px] transition-colors",
                search.status === f ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
              )}
            >
              {label(f)}
            </button>
          ))}
        </div>
        <div className="grow" />
        <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
          <SearchIcon size={15} aria-hidden="true" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("quality.ins.searchPlaceholder")}
            aria-label={t("quality.ins.searchPlaceholder")}
            data-list-search
            className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
          />
          {text && (
            <button type="button" aria-label={t("po.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
              <X size={14} />
            </button>
          )}
        </label>
      </div>

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("quality.ins.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.id}
          onSelect={(r) => setSearch({ id: r.id })}
          onClear={() => setSearch({ id: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(inspectionDetailQuery(r.id))}
          emptyTitle={t("quality.ins.emptyTitle")}
          emptyBody={t("quality.ins.emptyBody")}
          emptyAction={
            <button
              type="button"
              onClick={() => setSearch({ status: "all", q: "", id: undefined, ...noView })}
              className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
            >
              {t("list.clearFilters")}
            </button>
          }
          minWidth={860}
          tools={listTools}
          view={view}
        />
        <DetailPanel
          label={t("quality.ins.detailLabel")}
          open={!!search.id}
          placeholder={<DetailPlaceholder title={t("quality.ins.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {search.id && <InspectionPanel key={search.id} id={search.id} onClose={() => setSearch({ id: undefined })} onOpen={(id) => setSearch({ id })} />}
        </DetailPanel>
      </div>
    </div>
  );
}
